import { Link, useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import {
  LayoutGrid,
  Activity,
  Server,
  Network,
  Cpu,
  Boxes,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { GlobalSearch } from '@/components/Search';

const tabs = [
  { href: '/', label: 'Overview', icon: LayoutGrid },
  { href: '/proxmox', label: 'Proxmox', icon: Server },
  { href: '/services', label: 'Services', icon: Network },
  { href: '/integrations', label: 'Apps', icon: Boxes },
  { href: '/events', label: 'Events', icon: Activity },
  { href: '/baremetal', label: 'Baremetal', icon: Cpu },
  { href: '/launcher', label: 'Launcher', icon: Activity },
] as const;

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-card/40 backdrop-blur sticky top-0 z-20">
        <div className="container flex items-center justify-between h-14 gap-4">
          <Link href="/">
            <a className="flex items-center gap-2 font-semibold hover:opacity-80 transition-opacity whitespace-nowrap">
              <span className="text-primary text-xl">⌂</span>
              <span>home.emkacz.dev</span>
            </a>
          </Link>
          <div className="flex-1 max-w-md">
            <GlobalSearch />
          </div>
          <nav className="flex items-center gap-1">
            {tabs.map((tab) => (
              <NavLink key={tab.href} {...tab} />
            ))}
          </nav>
        </div>
      </header>
      <main className="flex-1 container py-6">{children}</main>
      <footer className="border-t border-border py-3 text-xs text-muted-foreground">
        <div className="container flex justify-between">
          <span>ops dashboard · auth via Authelia</span>
          <span>v0.1.0</span>
        </div>
      </footer>
    </div>
  );
}

function NavLink({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: LucideIcon;
}) {
  const [location] = useLocation();
  const active = location === href || (href === '/' && location === '/');
  return (
    <Link href={href}>
      <a
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors',
          active
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
        )}
      >
        <Icon className="h-4 w-4" />
        {label}
      </a>
    </Link>
  );
}
